import React from 'react';
import type { FieldValues } from 'react-hook-form';

/**
 * Whether a form's values differ from the ones it last settled on.
 *
 * <p>Used in place of react-hook-form's `formState.isDirty`, which both forms
 * on this page were misreporting in opposite directions:
 *
 * <ul>
 *   <li>The address form's mount-time mailing sync writes with `shouldDirty`,
 *       so it read dirty before the user had touched anything -- and with a
 *       single save bar keyed on it, the bar was up on page load.
 *   <li>The details form read dirty after a `reset()` even with `dirtyFields`
 *       empty and every value restored, so Discard left the bar up.
 * </ul>
 *
 * <p>The baseline is taken a tick after mount and rebased on every successful
 * save and on Discard. Both the delay and reading through `readValues` matter:
 * a reset or a sync lands over more than one render, so a synchronous read
 * catches the old values, and a baseline built from a caller's own object had
 * its keys in a different order from the form's, so the two never matched.
 */
export function useSettledDirty(
  values: FieldValues,
  readValues: () => FieldValues
) {
  const baseline = React.useRef<string | null>(null);
  const [, forceCompare] = React.useState(0);

  const rebaseBaseline = React.useCallback(() => {
    window.setTimeout(() => {
      baseline.current = JSON.stringify(readValues());
      forceCompare((count) => count + 1);
    }, 0);
  }, [readValues]);

  React.useEffect(() => {
    rebaseBaseline();
  }, [rebaseBaseline]);

  // Until the baseline is taken nothing counts as changed, which is correct:
  // the user cannot have edited yet.
  const isDirty =
    baseline.current !== null && JSON.stringify(values) !== baseline.current;

  return { isDirty, rebaseBaseline };
}

export default useSettledDirty;
